// Each transform property is represented by a number and a unit.
// The unit might be '', 'px', '%', 'deg', 'rad', etc.
import { NumberUtils } from './utils.number';

export interface TransformValue {
	value: number;
	unit: string;
}

export interface ParseTransformResult {
	translateX?: TransformValue;
	translateY?: TransformValue;
	scaleX?: TransformValue;
	scaleY?: TransformValue;
	rotate?: TransformValue;
	skewX?: TransformValue;
	skewY?: TransformValue;
	matrix?: {
		a: number;
		b: number;
		c: number;
		d: number;
		e: number;
		f: number;
	};
}

export class CSSUtils {
	/**
	 *  Parses a CSS transform string into an object with individual transform properties.
	 * @param transformString
	 * @returns
	 */
	public static parseTransform(transformString: string): ParseTransformResult {
		const result: ParseTransformResult = {};

		const transformRegex = /([a-zA-Z][a-zA-Z0-9]*)\(([^)]*)\)/g;

		function parseValue(valueStr: string, defaultUnit: string = ''): TransformValue {
			const valueUnitRegex = /^(-?\d*\.?\d+)([a-z%]*)$/i;
			const match = valueStr.trim().match(valueUnitRegex);
			if (match) {
				const numericVal = parseFloat(match[1]);
				const unitVal = match[2] || defaultUnit;
				return { value: numericVal, unit: unitVal };
			} else {
				// If we can't parse it as a number, fallback to 0 and given default unit
				return { value: 0, unit: defaultUnit };
			}
		}

		function setProp(prop: keyof ParseTransformResult, valObj: TransformValue) {
			if (valObj && prop !== 'matrix') {
				result[prop] = valObj;
			}
		}

		let match: RegExpExecArray | null;
		while ((match = transformRegex.exec(transformString)) !== null) {
			const fnName = match[1].toLowerCase();
			const argsStr = match[2].trim();
			const args = argsStr.split(',').map(arg => arg.trim());

			switch (fnName) {
				case 'translatex': {
					const val = parseValue(args[0], 'px');
					setProp('translateX', val);
					break;
				}
				case 'translatey': {
					const val = parseValue(args[0], 'px');
					setProp('translateY', val);
					break;
				}
				case 'translate3d':
				case 'translate': {
					// translate(x, y?) defaults y to 0 if not provided
					const xVal = parseValue(args[0], 'px');
					setProp('translateX', xVal);
					const yVal = args[1] ? parseValue(args[1], 'px') : { value: 0, unit: 'px' };
					setProp('translateY', yVal);
					break;
				}
				case 'scalex': {
					const val = parseValue(args[0], '');
					setProp('scaleX', val);
					break;
				}
				case 'scaley': {
					const val = parseValue(args[0], '');
					setProp('scaleY', val);
					break;
				}
				case 'scale': {
					// scale(sx, sy?) if sy not given, sy = sx
					const sxVal = parseValue(args[0], '');
					setProp('scaleX', sxVal);
					const syVal = args[1] ? parseValue(args[1], '') : { ...sxVal };
					setProp('scaleY', syVal);
					break;
				}
				case 'rotate': {
					const val = parseValue(args[0], 'deg');
					setProp('rotate', val);
					break;
				}
				case 'skewx': {
					const val = parseValue(args[0], 'deg');
					setProp('skewX', val);
					break;
				}
				case 'skewy': {
					const val = parseValue(args[0], 'deg');
					setProp('skewY', val);
					break;
				}
				case 'skew': {
					// skew(xAngle, yAngle?) if y not given, y = 0deg
					const xVal = parseValue(args[0], 'deg');
					setProp('skewX', xVal);
					const yVal = args[1] ? parseValue(args[1], 'deg') : { value: 0, unit: 'deg' };
					setProp('skewY', yVal);
					break;
				}
				case 'matrix': {
					// matrix(a, b, c, d, e, f)
					if (args.length === 6) {
						result.matrix = {
							a: parseFloat(args[0]) || 0,
							b: parseFloat(args[1]) || 0,
							c: parseFloat(args[2]) || 0,
							d: parseFloat(args[3]) || 0,
							e: parseFloat(args[4]) || 0,
							f: parseFloat(args[5]) || 0
						};
					}
					break;
				}
				default:
					// Ignore unsupported transforms
					break;
			}
		}

		return result;
	}

	/**
	 * Transforms an object with individual transform properties into a CSS transform string.
	 */
	public static transformObjectToString(transformObj: ParseTransformResult): string {
		const parts: string[] = [];

		// Helper to format a TransformValue into a string "value+unit"
		function valToStr(v: TransformValue | undefined): string {
			if (!v) return '';
			return `${v.value}${v.unit}`;
		}

		// Translate
		const tX = transformObj.translateX;
		const tY = transformObj.translateY;
		if (tX && tY) {
			parts.push(`translate(${valToStr(tX)}, ${valToStr(tY)})`);
		} else if (tX) {
			parts.push(`translateX(${valToStr(tX)})`);
		} else if (tY) {
			parts.push(`translateY(${valToStr(tY)})`);
		}

		// Scale
		const sX = transformObj.scaleX;
		const sY = transformObj.scaleY;
		if (sX && sY) {
			// If both scales present and identical, use scale(x)
			if (sX.value === sY.value && sX.unit === sY.unit) {
				parts.push(`scale(${valToStr(sX)})`);
			} else {
				// scaleX and scaleY differ
				parts.push(`scale(${valToStr(sX)}, ${valToStr(sY)})`);
			}
		} else if (sX) {
			// Only scaleX
			parts.push(`scaleX(${valToStr(sX)})`);
		} else if (sY) {
			// Only scaleY
			parts.push(`scaleY(${valToStr(sY)})`);
		}

		// Rotate
		const r = transformObj.rotate;
		if (r) {
			parts.push(`rotate(${valToStr(r)})`);
		}

		// Skew
		const kX = transformObj.skewX;
		const kY = transformObj.skewY;
		if (kX && kY) {
			parts.push(`skew(${valToStr(kX)}, ${valToStr(kY)})`);
		} else if (kX) {
			parts.push(`skewX(${valToStr(kX)})`);
		} else if (kY) {
			parts.push(`skewY(${valToStr(kY)})`);
		}

		// Matrix - should be first if present to preserve transform order
		if (transformObj.matrix) {
			const m = transformObj.matrix;
			const matrixStr = `matrix(${m.a}, ${m.b}, ${m.c}, ${m.d}, ${m.e}, ${m.f})`;
			// Insert matrix at the beginning to preserve transform order
			parts.unshift(matrixStr);
		}

		return parts.join(' ');
	}

	public static parseTransformOrigin(transformOrigin: string): { x: number; y: number; z: number } {
		const [x, y, z] = transformOrigin.split(' ').map(parseFloat);
		return { x, y, z: isNaN(z) ? 0 : z };
	}

	public static convertTransformUnit(transformProperties: ParseTransformResult, element: HTMLElement, targetUnit = 'px') {
		if (!transformProperties) {
			return;
		}

		transformProperties = { ...transformProperties };

		const parentDimensions: { clientWidth: number; clientHeight: number } = this.getFirstParentWithDimensions(element);

		for (const key in transformProperties) {
			const value = transformProperties[key];
			if (value.unit !== targetUnit && key.includes('translate')) {
				let valueWithoutUnit = value.value;

				if (targetUnit === '%' || targetUnit.startsWith('cq')) {
					// Convert top or height units to cqh.  Convert left or width units to cqw.
					valueWithoutUnit = `${NumberUtils.toPercentage(Number(value.value), key === 'translateX' ? parentDimensions.clientWidth : parentDimensions.clientHeight, 4)}`;
					if (targetUnit.startsWith('cq') && key === 'translateX') {
						targetUnit = 'cqw';
					} else {
						targetUnit = 'cqh';
					}
				} else {
					valueWithoutUnit = `${NumberUtils.valueFromPercentage(Number(valueWithoutUnit), key === 'translateX' ? parentDimensions.clientWidth : parentDimensions.clientHeight, 4)}`;
				}

				transformProperties[key] = { value: Number(valueWithoutUnit), unit: targetUnit };
			}
		}

		return transformProperties;
	}

	public static convertDimensionUnit(
		dimensions: { width?: string; height?: string },
		element: HTMLElement,
		targetUnit: string = 'px'
	): { width?: TransformValue; height?: TransformValue } {
		let result = {};
		const parentDimensions = this.getFirstParentWithDimensions(element);

		for (const key in dimensions) {
			const value = dimensions[key];
			const unit = value.match(/(cqw|cqh|px|%)$/i)?.[0]?.toLowerCase() || 'px';
			if (unit !== targetUnit) {
				let valueWithoutUnit = parseFloat(value);

				if (targetUnit === '%' || targetUnit.startsWith('cq')) {
					// Convert height units to cqh. Convert width units to cqw.
					valueWithoutUnit = NumberUtils.toPercentage(
						Number(valueWithoutUnit),
						key === 'width' ? parentDimensions.clientWidth : parentDimensions.clientHeight,
						4
					);
					if (targetUnit.startsWith('cq')) {
						targetUnit = key === 'width' ? 'cqw' : 'cqh';
					}
				} else {
					valueWithoutUnit = NumberUtils.valueFromPercentage(
						Number(valueWithoutUnit),
						key === 'width' ? parentDimensions.clientWidth : parentDimensions.clientHeight,
						4
					);
				}
				result[key] = {
					value: Number(valueWithoutUnit),
					unit: targetUnit
				};
			} else {
				result[key] = {
					value: value.value,
					unit: targetUnit
				};
			}
		}

		return result;
	}

	/**
	 * Builds a layer height style from a pixel height.
	 * A layer with a locked aspect ratio stores its height against the container width (cqw), like its width,
	 * so the ratio survives a change to the container's aspect ratio. Other layers use the container height (cqh).
	 */
	public static toLayerHeight(
		heightPx: number,
		container: { width: number; height: number },
		lockAspectRatio = false,
		decimals?: number
	): string {
		if (lockAspectRatio) {
			return `${NumberUtils.toPercentage(heightPx, container.width, decimals)}cqw`;
		}
		return `${NumberUtils.toPercentage(heightPx, container.height, decimals)}cqh`;
	}

	/**
	 * Re-expresses a layer height on the axis {@link toLayerHeight} uses for the given lock state, keeping its rendered size.
	 * Returns null when the height is already on that axis or cannot be converted (auto, calc(), em, etc.).
	 */
	public static convertLayerHeightAxis(
		height: string | number | undefined,
		container: { width: number; height: number },
		lockAspectRatio: boolean
	): string | null {
		if (height === undefined || height === null || height === '') {
			return null;
		}

		const match = String(height)
			.trim()
			.match(/^(-?\d*\.?\d+)(px|cqw|cqh|%)?$/i);
		if (!match || !container.width || !container.height) {
			return null;
		}

		const value = Number(match[1]);
		const unit = (match[2] || 'px').toLowerCase();
		if (unit === (lockAspectRatio ? 'cqw' : 'cqh')) {
			return null;
		}

		const heightPx = unit === 'cqw' ? (value / 100) * container.width : unit === 'px' ? value : (value / 100) * container.height;
		return this.toLayerHeight(heightPx, container, lockAspectRatio, 4);
	}

	/**
	 * Recursively traverses up the DOM tree to find the first parent element that has defined clientWidth and clientHeight.
	 * It also accounts for any padding on the parent elements to return the available dimensions for the child element.
	 * If no parent with dimensions is found, it returns { clientWidth: 0, clientHeight: 0 }.
	 * @param element
	 * @param dimensions
	 * @private
	 */
	private static getFirstParentWithDimensions(
		element: HTMLElement,
		dimensions?: { clientWidth: number; clientHeight: number }
	): { clientWidth: number; clientHeight: number } {
		if (!dimensions) {
			dimensions = { clientWidth: 0, clientHeight: 0 };
		}
		if (!element || !element.parentElement) {
			return dimensions;
		}

		const parentElement = element.parentElement;

		if (parentElement.clientWidth > 0 || parentElement.clientHeight > 0) {
			const computed = getComputedStyle(parentElement) ?? null;
			const padding = {
				left: parseFloat(computed?.paddingLeft) || 0,
				right: parseFloat(computed?.paddingRight) || 0,
				top: parseFloat(computed?.paddingTop) || 0,
				bottom: parseFloat(computed?.paddingBottom) || 0
			};

			const width = parentElement.clientWidth - padding.left - padding.right;
			const height = parentElement.clientHeight - padding.top - padding.bottom;

			if (width > 0) {
				dimensions.clientWidth = width;
			}
			if (height > 0) {
				dimensions.clientHeight = height;
			}
		}

		if (dimensions.clientWidth > 0 && dimensions.clientHeight > 0) {
			return dimensions;
		}
		return this.getFirstParentWithDimensions(parentElement, dimensions);
	}

	/**
	 * Checks if the given element has any inline padding styles defined.
	 * @param element
	 */
	public static elementHasInlinePadding(element: HTMLElement): boolean {
		if (!element) {
			return false;
		}

		return !!(
			element?.style?.padding ||
			element?.style?.paddingLeft ||
			element?.style?.paddingRight ||
			element?.style?.paddingTop ||
			element?.style?.paddingBottom
		);
	}

	public static getValueFromUnit(value: string | number, expectedUnit: '' | 'px' | 'rem' | 'em' | 'vh' | 'vw' | '%'): number | null {
		if (!value) {
			return null;
		}
		if (typeof value !== 'string') {
			// If already a number and no unit is expected, return it.
			if (expectedUnit === '' && typeof value === 'number') {
				return value;
			} else return null;
		}

		const match = value.match(/^([\d.]+)([a-zA-Z%]*)$/);

		if (!match) return null;

		const [, num, unit] = match;

		return unit === expectedUnit ? parseFloat(num) : null;
	}
}
